import React, { Fragment, useEffect, useState } from 'react';

import SchemaControlBar from './schemaControlBar';
import { Button, InfoBanner } from '../../commons/utils';
import { _13digitBarcode, isScaleCabinet, isScale2Cabinet, isScaleShelfType } from '../utils';
import PlanogramImageStacking from '../modals/planogramImageStacking';
import ConfirmationModal from '../modals/confirmationModal';
import ApiRequestModal from '../modals/ApiRequestModal';
import PlanogramImageModal from '../modals/planogramImageModal';

const MAX_CABINET_SHELVES_COUNT = 8;
const MAX_CABINET_SLOT_COUNT = 8;

const Planogram = ({
    toggleViewMode,
    activeViewMode,
    t,
    cabinetRefillSchema,
    planogramProductData,
    allProducts,
    updateCabinetRefillSchema,
    selectedCabinet,
    fetchRefillRules,
    showViewModeToggle,
    refillPlan,
    allowEditing = true,
    saveRefillPlanogram,
    planogramImageInStore,
    uploadPlanogramImage,
    changeRefillPlanogramImageData,
    isAdmin,
    savePlanogram,
    planogramStore,
    fetchPlanogram,
    deletePlanogram,
}) => {
    const [planogramData, setPlanogramData] = useState({});
    const [allProductsData, setAllProductsData] = useState([]);
    const [imageStackingModal, setImageStackingModal] = useState({ open: false, slotData: {}, shelfNumber: null });
    const [productFilter, setProductFilter] = useState('all');
    const [prestine, setPrestine] = useState(true);
    const [productSearchQuery, setProductSearchQuery] = useState('');
    const [savePlanogramModal, setSavePlanogramModal] = useState(false);
    const [deletePlanogramModal, setDeletePlanogramModal] = useState(false);
    const [openPlanogramImageModal, setOpenPlanogramImagemModal] = useState(false);
    const [planogramImageUrl, setPlanogramImageUrl] = useState(null);

    useEffect(() => {
        if (!allProducts.length) return;
        let products = [...allProducts];
        products = products
            .filter(product => product.status === 2)
            .map(product => {
                return { ...product, barcode: _13digitBarcode(product.barcodeType, product.barcode) };
            });

        setAllProductsData(products);
    }, [allProducts]);

    useEffect(() => {
        setPlanogramData(planogramProductData);
    }, [planogramProductData]);

    useEffect(() => {
        const { rule } = cabinetRefillSchema;
        const imageUrl = rule.planogramImageUrl;
        setPlanogramImageUrl(imageUrl);
    }, [cabinetRefillSchema]);

    /**
     *
     * @param {string} action plus | minus
     * @returns
     */
    const onShelvesCountChange = action => {
        if (!isAdmin) return;
        const data = { ...planogramData };
        if (action === 'plus') {
            if (data.shelvesCount >= MAX_CABINET_SHELVES_COUNT) return;
            data.shelvesCount += 1;
            data.shelves.push({
                shelf: data.shelvesCount,
                ...(isScale2Cabinet(selectedCabinet) && { type: 'scale2' }),
                slots: [
                    { slot: 1, products: [] },
                    { slot: 2, products: [] },
                    { slot: 3, products: [] },
                    { slot: 4, products: [] },
                    { slot: 5, products: [] },
                ],
            });
        } else {
            if (data.shelvesCount === 1) return;
            //always remove the last shelf in array
            //remove all the products, if have been placed in the shelf already
            data.shelves = data.shelves.filter(shelf => shelf.shelf !== data.shelvesCount);
            data.shelvesCount -= 1;
        }
        setPrestine(false);
        setPlanogramData(data);
    };

    /**
     *
     * @param {*} shelfNumber 1|2|3...8
     * @param {*} action 'plus' | 'minus'
     * @returns
     */
    const onSlotCountChange = (shelfNumber, action) => {
        if (!isAdmin) return;

        const data = { ...planogramData };
        const selectedShelf = data.shelves.find(shelf => shelf.shelf === shelfNumber);
        selectedShelf.slots = selectedShelf.slots || [];
        const lastSlotNumber = selectedShelf.slots.reduce((max, slotData) => Math.max(max, slotData.slot), 0);
        if (action === 'plus') {
            if (selectedShelf.slots.length >= MAX_CABINET_SLOT_COUNT) return;
            selectedShelf.slots.push({
                slot: lastSlotNumber + 1,
                products: [],
            });
        } else {
            if (selectedShelf.slots.length === 0) return;

            //remove the last slot and product data from the shelf
            selectedShelf.slots = selectedShelf.slots.filter(slotData => slotData.slot !== lastSlotNumber);
        }
        setPrestine(false);
        setPlanogramData(data);
    };

    const onProductDragged = (e, product) => {
        e.dataTransfer.setData('text/plain', JSON.stringify(product));
    };

    /**
     *
     * @param {*} e drag and drop event
     * @param {*} shelf the shelf number where product is dropped
     * @param {*} slot the slot number with in the shelf
     * @returns
     */
    const onProductDropped = (e, shelf, slot) => {
        if (!isAdmin) return;

        const transferData = e.dataTransfer.getData('text/plain');
        if (!transferData) return;

        const productDataDragged = JSON.parse(transferData);
        const data = { ...planogramData };
        const slots = data.shelves.find(s => s.shelf === shelf)?.slots;
        if (!slots.length || !slots) {
            slots.push({ slot, products: [productDataDragged.id] });
        } else {
            const selectedSlot = slots.find(s => s.slot === slot);

            if (selectedSlot) {
                //allow only one product for the scale shelf
                if (selectedSlot.products.length && isScaleShelf(shelf)) return;

                if (selectedSlot.products && selectedSlot.products.length >= 8) {
                    return;
                }

                selectedSlot.products.push(productDataDragged.id);
            } else {
                slots.push({ slot, products: [productDataDragged.id] });
            }
        }
        setPrestine(false);
        setPlanogramData(data);
    };

    /**
     *
     * @param {*} e drag-and-drop event
     * @param {*} shelf the shelf which on which drag happened
     * @param {*} slot the slot that was dragged
     */
    const onCabinetSlotDragged = (e, shelf, slot) => {
        if (!isAdmin) return;
        e.dataTransfer.setData('text/plain', JSON.stringify({ shelf, slot }));
    };

    /**
     * when user drags slot and drop on a product
     * @param {*} e drang-and-drop event
     * @param {*} product product upon which the product was dropped
     * @returns
     */
    const slotDropOnProduct = (e, product) => {
        if (!isAdmin) return;
        const transferData = e.dataTransfer.getData('text/plain');
        if (!transferData) return;

        const { shelf, slot } = JSON.parse(transferData);
        const data = { ...planogramData };
        const selectedShelf = data.shelves.find(shelfData => shelfData.shelf === shelf);
        if (!selectedShelf) return;
        const selectedSlot = selectedShelf.slots.find(slotData => slotData.slot === slot);
        if (!selectedSlot) {
            selectedShelf.slots.push({ slot, products: [product.id] });
        } else {
            selectedShelf.slots = selectedShelf.slots.map(slotData => {
                if (slotData.slot !== slot) return slotData;

                return { slot, products: [...slotData.products, product.id] };
            });
        }
        setPrestine(false);
        setPlanogramData(data);
    };

    /**
     * when delete icon is clicked on slot
     * @param {*} shelf
     * @param {*} slot
     */
    const removeProductFromSlot = (shelf, slot) => {
        if (!isAdmin) return;
        if (!allowEditing) return;

        setPrestine(false);
        const data = { ...planogramData };

        const selectedShelf = data.shelves.find(shelfData => shelfData.shelf === shelf);
        selectedShelf.slots = selectedShelf.slots.filter(slotData => slotData.slot !== slot);

        setPlanogramData(data);
    };

    /**
     * look for search query or allProducts || schema products filter and send the filtered product to view
     * @returns array of products
     */
    const getProductsForView = () => {
        if (productFilter === 'all' && !productSearchQuery) return allProductsData;

        let productsToShow = [];
        if (productFilter === 'schema') {
            const { rule } = cabinetRefillSchema;
            const schemaProductData = rule.productData || [];
            schemaProductData.forEach(schemaProduct => {
                const product = allProductsData.find(p => p.barcode === _13digitBarcode(schemaProduct.barcodeType, schemaProduct.barcode));
                if (product) {
                    productsToShow.push({
                        name: product.name,
                        barcode: product.barcode,
                        imageUrl: product.imageUrl,
                        id: product.id,
                    });
                }
            });
        } else {
            productsToShow = [...allProductsData];
        }

        if (!productSearchQuery) return productsToShow;

        const query = productSearchQuery.toString().toLowerCase();
        return productsToShow.filter(product => {
            return (
                product.name
                    .toString()
                    .toLowerCase()
                    .includes(query) ||
                product.barcode
                    .toString()
                    .toLowerCase()
                    .includes(query)
            );
        });
    };

    /**
     * check if the current shelf in loop is a rfid shelf
     * @param {*} shelfNumber
     * @returns
     */
    const isScaleShelf = shelfNumber => {
        const allShelves = planogramData['shelves'];
        const currentShelf = allShelves.find(shelf => shelf.shelf === shelfNumber)?.type;
        return isScaleShelfType(currentShelf);
    };

    /**
     * updates refill shema with planogram data. removes planogram if the request type is delete
     * @param {*} requestType save | delete
     * @returns
     */
    const sendPlanogramToApi = (requestType = 'save') => {
        const { rule } = cabinetRefillSchema;
        if (!rule) return;

        if (requestType === 'save') {
            setSavePlanogramModal(true);
            // scale cabinets are merged, never overwritten: scale1 keeps its trained slots, scale2 keeps the
            // shelves, lanes and counts the cabinet writes from the weight service. Only rfid layouts overwrite.
            const shouldOverWriteData = !isScaleCabinet(selectedCabinet) && !isScale2Cabinet(selectedCabinet);
            //save planogram
            savePlanogram(selectedCabinet.deviceCode, planogramData, shouldOverWriteData);
            // also need to update the schema, since planogram image need to be updated
            const productData = rule.productData || [];
            const schemaId = rule.id;
            const deviceCode = selectedCabinet.deviceCode;
            updateCabinetRefillSchema(
                {
                    productData,
                    ...(planogramImageUrl && { planogramImageUrl }),
                },
                schemaId,
                deviceCode
            );
        } else {
            // refetch only after the delete has completed, otherwise the fetch can race it and bring the old
            // planogram back
            deletePlanogram(selectedCabinet.deviceCode).then(() => fetchPlanogram(selectedCabinet.deviceCode));
        }
    };

    return (
        <Fragment>
            <ApiRequestModal
                isOpen={savePlanogramModal}
                data={planogramStore}
                closeModal={() => {
                    fetchPlanogram(selectedCabinet.deviceCode);
                    fetchRefillRules();
                    setSavePlanogramModal(false);
                }}
                successMessage={t('refill:planogramSaved')}
                t={t}
            />
            <PlanogramImageStacking
                isOpen={imageStackingModal.open}
                close={() => {
                    setImageStackingModal({ open: false, slotData: {}, shelfNumber: null });
                }}
                cabinetSlotData={imageStackingModal.slotData}
                shelfNumber={imageStackingModal.shelfNumber}
                allProducts={allProductsData}
                t={t}
            />
            <PlanogramImageModal
                isOpen={openPlanogramImageModal}
                closeModal={() => setOpenPlanogramImagemModal(false)}
                t={t}
                planogramImageInStore={planogramImageInStore}
                uploadPlanogramImage={uploadPlanogramImage}
                planogramData={planogramData}
                planogramImageUrl={planogramImageUrl}
                planogramImageData={imageUrl => {
                    setPrestine(false);
                    if (refillPlan) {
                        changeRefillPlanogramImageData(imageUrl);
                    } else {
                        setPlanogramImageUrl(imageUrl);
                    }
                }}
            />
            {allowEditing && (
                <SchemaControlBar
                    saveRefillSchema={() => {
                        if (refillPlan) {
                            saveRefillPlanogram(planogramData);
                        } else {
                            sendPlanogramToApi('save');
                        }
                    }}
                    deleteRefillSchema={() => setDeletePlanogramModal(true)}
                    schema={cabinetRefillSchema}
                    prestine={prestine}
                    searchQuery={query => setProductSearchQuery(query)}
                    t={t}
                    toggleViewMode={mode => {
                        toggleViewMode(mode);
                    }}
                    activeViewMode={activeViewMode}
                    showProductFilter={true}
                    productFilterChange={type => setProductFilter(type)}
                    showViewModeToggle={showViewModeToggle}
                    refillPlan={refillPlan}
                    isAdmin={isAdmin}
                />
            )}

            <ConfirmationModal
                isOpen={deletePlanogramModal}
                cancel={() => setDeletePlanogramModal(false)}
                message={t('deletePlanogram')}
                t={t}
                confirmDelete={() => {
                    sendPlanogramToApi('delete');
                    setDeletePlanogramModal(false);
                }}
            />

            <div className="planogram">
                <div className="planogram__figure">
                    {allowEditing && !isScaleCabinet(selectedCabinet) && (
                        <Fragment>
                            <div>{t('refill:numberOfShelves')}</div>
                            <div className="planogram__setting">
                                <div className="planogram__setting-btn" onClick={() => onShelvesCountChange('minus')}>
                                    -
                                </div>
                                <input type="number" name="shelf count" value={planogramData.shelvesCount || 1} disabled />
                                <div className="planogram__setting-btn" onClick={() => onShelvesCountChange('plus')}>
                                    +
                                </div>
                            </div>
                        </Fragment>
                    )}

                    <div className="planogram__cabinet">
                        <div className="planogram__cabinet-screen">{t('refill:screen')}</div>
                        <div className="planogram__cabinet-shelves">
                            {(() => {
                                const allShelves = [];
                                // iterate the shelves that exist, by shelf number: identical to 1..shelvesCount for
                                // rfid/scale1 planograms, and tolerant of gaps in cabinet-written scale2 planograms
                                const shelvesSorted = [...(planogramData.shelves || [])].sort((a, b) => a.shelf - b.shelf);
                                shelvesSorted.forEach(shelfEntry => {
                                    const i = shelfEntry.shelf;
                                    allShelves.push(
                                        <div className="planogram__cabinet-shelf" key={`helf-${i}`}>
                                            {allowEditing && (!isScaleShelf(i) || shelfEntry.type === 'scale2') && (
                                                <Fragment>
                                                    <div
                                                        className="planogram__cabinet-shelf-btn"
                                                        onClick={() => {
                                                            onSlotCountChange(i, 'minus');
                                                        }}
                                                    >
                                                        -
                                                    </div>
                                                    <div
                                                        className="planogram__cabinet-shelf-btn"
                                                        onClick={() => {
                                                            onSlotCountChange(i, 'plus');
                                                        }}
                                                    >
                                                        +
                                                    </div>
                                                </Fragment>
                                            )}
                                            {isScaleShelf(i) && (
                                                <div
                                                    className={`planogram__cabinet-shelf-btn planogram__cabinet-shelf-btn--scale ${
                                                        allowEditing && shelfEntry.type === 'scale2' ? 'planogram__cabinet-shelf-btn--scale-inline' : ''
                                                    }`}
                                                    title={shelfEntry.type === 'scale2' ? shelfEntry.type : undefined}
                                                >
                                                    <svg>
                                                        <use xlinkHref="#icon-balance-scale"></use>
                                                    </svg>
                                                </div>
                                            )}

                                            <div className="planogram__slots">
                                                {(() => {
                                                    const allSlots = [];
                                                    const shelf = shelfEntry;
                                                    const slots = shelf.slots || [];
                                                    const isScale2Shelf = shelf.type === 'scale2';
                                                    // rfid/scale1 slots are 1..n; scale2 slots are numbered by lane position,
                                                    // so render the ones that exist
                                                    const slotNumbers = isScale2Shelf
                                                        ? slots.map(s => s.slot).sort((a, b) => a - b)
                                                        : Array.from({ length: slots.length }, (_, k) => k + 1);
                                                    slotNumbers.forEach(j => {
                                                        if (!slots.length) {
                                                            allSlots.push(
                                                                <div
                                                                    className="planogram__slots-item"
                                                                    draggable={true}
                                                                    key={`slot-${i}-${j}`}
                                                                    onDrop={e => onProductDropped(e, i, j)}
                                                                    onDragOver={e => e.preventDefault()}
                                                                    onDragStart={e => onCabinetSlotDragged(e, i, j)}
                                                                >
                                                                    {j}
                                                                </div>
                                                            );
                                                        } else {
                                                            const selectedSlot = slots.find(productSlot => productSlot.slot === j);
                                                            const hasPlan = selectedSlot && selectedSlot.products && selectedSlot.products.length;

                                                            if (isScale2Shelf && selectedSlot && !hasPlan && selectedSlot.productId) {
                                                                // scale2 lane with a product on it but no plan yet: show what the cabinet
                                                                // reports (read from productId); dropping a product here sets the plan
                                                                const actualProduct = allProductsData.find(product => product.id === selectedSlot.productId);
                                                                allSlots.push(
                                                                    <div
                                                                        className="planogram__slots-item planogram__slots-item--actual"
                                                                        draggable={true}
                                                                        key={`slot-${i}-${j}`}
                                                                        onDrop={e => onProductDropped(e, i, j)}
                                                                        onDragOver={e => e.preventDefault()}
                                                                        title={`${actualProduct ? actualProduct.name : selectedSlot.barcode || ''} (${t('refill:onLane')})`}
                                                                    >
                                                                        {actualProduct && actualProduct.imageUrl ? (
                                                                            <img src={actualProduct.imageUrl} alt="" />
                                                                        ) : (
                                                                            <svg className="planogram__slots-item-cube">
                                                                                <use xlinkHref="#icon-cube"></use>
                                                                            </svg>
                                                                        )}
                                                                    </div>
                                                                );
                                                            } else if (!selectedSlot || !hasPlan) {
                                                                allSlots.push(
                                                                    <div
                                                                        className="planogram__slots-item"
                                                                        draggable={true}
                                                                        key={`slot-${i}-${j}`}
                                                                        onDrop={e => onProductDropped(e, i, j)}
                                                                        onDragOver={e => e.preventDefault()}
                                                                        onDragStart={e => onCabinetSlotDragged(e, i, j)}
                                                                    >
                                                                        {j}
                                                                    </div>
                                                                );
                                                            } else {
                                                                if (selectedSlot.products.length === 1) {
                                                                    const selectedProduct = allProductsData.find(
                                                                        product => product.id === selectedSlot.products[0]
                                                                    );
                                                                    selectedProduct &&
                                                                        allSlots.push(
                                                                            <div
                                                                                className="planogram__slots-item"
                                                                                draggable={true}
                                                                                key={`slot-${i}-${j}`}
                                                                                onDrop={e => onProductDropped(e, i, j)}
                                                                                onDragOver={e => e.preventDefault()}
                                                                                title={selectedProduct.name}
                                                                            >
                                                                                {selectedProduct.imageUrl ? (
                                                                                    <img src={selectedProduct.imageUrl} alt="" />
                                                                                ) : (
                                                                                    <svg className="planogram__slots-item-cube">
                                                                                        <use xlinkHref="#icon-cube"></use>
                                                                                    </svg>
                                                                                )}
                                                                                {allowEditing && (
                                                                                    <svg
                                                                                        className="planogram__slots-item-close"
                                                                                        onClick={() => removeProductFromSlot(i, j, selectedProduct)}
                                                                                    >
                                                                                        <use xlinkHref="#icon-trash-2"></use>
                                                                                    </svg>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                } else {
                                                                    //mulitple products in the slot, show only the last added
                                                                    //show stack icon
                                                                    const lastAddedBarcode = selectedSlot.products[selectedSlot.products.length - 1];
                                                                    const selectedProduct = allProductsData.find(product => product.id === lastAddedBarcode);
                                                                    selectedProduct &&
                                                                        allSlots.push(
                                                                            <div
                                                                                className="planogram__slots-item"
                                                                                draggable={true}
                                                                                key={`slot-${i}-${j}`}
                                                                                onDrop={e => onProductDropped(e, i, j)}
                                                                                onDragOver={e => e.preventDefault()}
                                                                            >
                                                                                {selectedProduct.imageUrl ? (
                                                                                    <img src={selectedProduct.imageUrl} alt="" />
                                                                                ) : (
                                                                                    <svg className="planogram__slots-item-cube">
                                                                                        <use xlinkHref="#icon-cube"></use>
                                                                                    </svg>
                                                                                )}

                                                                                <svg
                                                                                    className="planogram__slots-item-stack"
                                                                                    onClick={() =>
                                                                                        setImageStackingModal({
                                                                                            open: true,
                                                                                            slotData: selectedSlot,
                                                                                            shelfNumber: i,
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    <use xlinkHref="#icon-stack"></use>
                                                                                </svg>
                                                                                {allowEditing && (
                                                                                    <svg
                                                                                        className="planogram__slots-item-close"
                                                                                        onClick={() => removeProductFromSlot(i, j, selectedProduct)}
                                                                                    >
                                                                                        <use xlinkHref="#icon-trash-2"></use>
                                                                                    </svg>
                                                                                )}
                                                                                <span
                                                                                    className="planogram__slots-item-count"
                                                                                    onClick={() =>
                                                                                        setImageStackingModal({
                                                                                            open: true,
                                                                                            slotData: selectedSlot,
                                                                                            shelfNumber: i,
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    {selectedSlot.products.length}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                }
                                                            }
                                                        }
                                                    });
                                                    return allSlots;
                                                })()}
                                            </div>
                                        </div>
                                    );
                                });
                                return allShelves;
                            })()}
                        </div>
                        {(() => {
                            const { rule } = cabinetRefillSchema;
                            const shouldShowPlanogramImage =
                                (!refillPlan && rule.id && planogramData && Object.keys(planogramData)) || (refillPlan && allowEditing);

                            if (shouldShowPlanogramImage) {
                                return (
                                    <div className="planogram__image">
                                        <InfoBanner message={t('refill:planogramImageInfo')} />
                                        <img src={planogramImageUrl} alt="" />
                                        {isAdmin && (
                                            <Button
                                                type="add"
                                                text={`${planogramImageUrl ? t('refill:editPlanogramImage') : t('refill:addPlanogramImage')}`}
                                                onClick={() => setOpenPlanogramImagemModal(true)}
                                            />
                                        )}
                                    </div>
                                );
                            }
                        })()}
                    </div>
                </div>
                {allowEditing && (
                    <div className="planogram__products">
                        <div className="planogram__products-container">
                            <InfoBanner message={t('dragInPlanogram')} className="planogram__products-banner" />
                            <div className="planogram__products-items">
                                {getProductsForView().map(product => {
                                    return (
                                        <div
                                            className="planogram__products-item"
                                            onDragStart={e => onProductDragged(e, product)}
                                            onDragOver={e => e.preventDefault()}
                                            draggable={true}
                                            onDrop={e => slotDropOnProduct(e, product)}
                                            key={product.barcode}
                                        >
                                            {product.imageUrl ? (
                                                <img src={product.imageUrl} alt="" />
                                            ) : (
                                                <svg className="planogram__slots-item-noimg">
                                                    <use xlinkHref="#icon-cube"></use>
                                                </svg>
                                            )}

                                            <div className="planogram__products-barcode">{product.barcode}</div>
                                            <div className="planogram__products-name">{product.name}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

export default Planogram;
